import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BarChart from './BarChart';
import { duration, fmt } from './adminFormat';

// Performance / load testing. The admin's own browser fires a configurable number of concurrent
// "simulated callers" at the real site and API endpoints and measures throughput, latency percentiles
// and errors. It runs entirely client-side (no backend endpoint), so it's limited by this machine and
// browser (roughly a few thousand requests a second over HTTP/2); for a harder capacity test than one
// browser can drive, a backend load-driver in the API repo would be the next step. Pre-launch, the only
// traffic is the owner's, so hammering the production API is their own infrastructure.

const DEFAULT_API_BASE = String(process.env.REACT_APP_API_BASE_URL || 'https://api.xn--omrdeskollen-vcb.nu').replace(/\/+$/, '');
const MAX_CALLERS = 500;
const SNAPSHOT_MS = 400;
const MAX_SAMPLES = 500000; // keep latency samples bounded on very long/fast runs
// Stockholm kommun has 127 RegSO areas (0180R001..0180R127); the per-area targets pick one at random each
// request so the test varies the query instead of hammering one cached row.
const STHLM_IDS = Array.from({ length: 127 }, (_, i) => `0180R${String(i + 1).padStart(3, '0')}`);
const HIST_EDGES = [1000000, 2000000, 3000000, 4000000, 5000000, 7000000, 10000000];
const RUNS_KEY = 'admin.perf.runs';
const CONFIG_KEY = 'admin.perf.config';

const pick = (list) => list[Math.floor(Math.random() * list.length)];
const sleep = (ms, signal) => new Promise((resolve) => {
    const id = window.setTimeout(resolve, ms);
    if (signal) signal.addEventListener('abort', () => { window.clearTimeout(id); resolve(); }, { once: true });
});

// The site's own origin (the static web host / CDN), i.e. drop an "admin." prefix from the current host.
const siteOrigin = () => {
    if (typeof window === 'undefined') return '';
    try {
        const url = new URL(window.location.origin);
        url.hostname = url.hostname.replace(/^admin\./, '');
        return url.origin;
    } catch (error) {
        return window.location.origin;
    }
};

// One entry per request kind a visitor makes. build() returns a fresh request each call so the per-area
// ones can randomise their id. `pool` (the full-listing target) is filled from a prep fetch before a run.
const buildTargets = (base, site, poolRef) => [
    {
        id: 'compact', label: 'Listings (compact)', hint: '/listings/active/compact — ~1.5 MB, every map visitor',
        build: () => ({ method: 'GET', url: `${base}/listings/active/compact` }),
    },
    {
        id: 'overlay', label: 'Area overlay', hint: '/getoverlay — all RegSO polygons (TopoJSON)',
        build: () => ({ method: 'GET', url: `${base}/getoverlay` }),
    },
    {
        id: 'data', label: 'Area statistics', hint: '/getdata — per-area stats',
        build: () => ({ method: 'GET', url: `${base}/getdata` }),
    },
    {
        id: 'regso', label: 'Listvy area page', hint: '/listings/active/regso/:id — one area, first page',
        build: () => ({ method: 'GET', url: `${base}/listings/active/regso/${pick(STHLM_IDS)}?offset=0&limit=20&forSale=true` }),
    },
    {
        id: 'filter', label: 'Listvy kommun search', hint: 'POST /listings/active/filter — Stockholm, first page (heavier query)',
        build: () => ({ method: 'POST', url: `${base}/listings/active/filter`, body: { kommun: 'Stockholm', offset: 0, limit: 20, forSale: true } }),
    },
    {
        id: 'histogram', label: 'Price histogram', hint: 'POST /listings/active/price-histogram — drawer slider',
        build: () => ({ method: 'POST', url: `${base}/listings/active/price-histogram`, body: { regsoIds: [pick(STHLM_IDS)], edges: HIST_EDGES, forSale: true } }),
    },
    {
        id: 'priceHistory', label: 'Area price trend', hint: '/price-history/area/:id — the area trend chip',
        build: () => ({ method: 'GET', url: `${base}/price-history/area/${pick(STHLM_IDS)}?type=apartment` }),
    },
    {
        id: 'full', label: 'Listing popup', hint: '/listings/active/full/:kind/:n — a random live listing',
        needsPool: true,
        build: () => ({ method: 'GET', url: `${base}/listings/active/full/${pick(poolRef.current)}` }),
    },
    {
        id: 'status', label: 'Travel status', hint: '/travel/status — a cheap health ping',
        build: () => ({ method: 'GET', url: `${base}/travel/status` }),
    },
    {
        id: 'site', label: 'Site page (HTML)', hint: `${site}/ — the web host / CDN (opaque, status not readable)`,
        build: () => ({ method: 'GET', url: `${site}/`, mode: 'no-cors' }),
    },
];

const DEFAULT_CONFIG = {
    apiBase: DEFAULT_API_BASE,
    callers: 10,
    model: 'duration', // 'duration' | 'count'
    durationS: 10,
    countPerCaller: 20,
    thinkMs: 0,
    rampS: 0,
    bypassCache: true,
    targets: ['compact'],
};

const readConfig = () => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(CONFIG_KEY) || '{}');
        return { ...DEFAULT_CONFIG, ...saved, targets: Array.isArray(saved.targets) ? saved.targets : DEFAULT_CONFIG.targets };
    } catch (error) {
        return { ...DEFAULT_CONFIG };
    }
};
const readRuns = () => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(RUNS_KEY) || '[]');
        return Array.isArray(saved) ? saved : [];
    } catch (error) {
        return [];
    }
};
const writeJson = (key, value) => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (error) { /* not remembered: fine */ }
};

const percentile = (sorted, p) => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))] : 0);
const ms = (value) => (value >= 1000 ? `${(value / 1000).toFixed(2)} s` : `${Math.round(value)} ms`);
const bytes = (value) => {
    if (!value) return '0 B';
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const freshMetrics = () => ({
    count: 0, ok: 0, fail: 0, bytesTotal: 0, sumDur: 0, inFlight: 0,
    durs: [], perTarget: new Map(), perSecond: new Map(), errors: new Map(), startAt: 0,
});

const PerformanceTab = () => {
    const [config, setConfig] = useState(readConfig);
    const [running, setRunning] = useState(false);
    const [live, setLive] = useState(null);
    const [result, setResult] = useState(null);
    const [runs, setRuns] = useState(readRuns);
    const [prepNote, setPrepNote] = useState('');

    const metricsRef = useRef(freshMetrics());
    const abortRef = useRef(null);
    const stopRef = useRef(false);
    const poolRef = useRef([]);
    const configRef = useRef(config);
    configRef.current = config;

    const targets = useMemo(() => buildTargets(String(config.apiBase || DEFAULT_API_BASE).replace(/\/+$/, ''), siteOrigin(), poolRef), [config.apiBase]);
    const targetById = useMemo(() => new Map(targets.map((t) => [t.id, t])), [targets]);

    useEffect(() => { writeJson(CONFIG_KEY, config); }, [config]);

    const set = (patch) => setConfig((prev) => ({ ...prev, ...patch }));
    const num = (value, min, max, fallback) => {
        const parsed = Number(String(value).replace(',', '.'));
        if (!Number.isFinite(parsed)) return fallback;
        return Math.max(min, Math.min(max, parsed));
    };
    const toggleTarget = (id) => setConfig((prev) => {
        const on = prev.targets.includes(id);
        return { ...prev, targets: on ? prev.targets.filter((t) => t !== id) : [...prev.targets, id] };
    });

    const activeTargets = config.targets.map((id) => targetById.get(id)).filter(Boolean);
    const plannedTotal = config.model === 'count' ? config.callers * config.countPerCaller : null;

    // Snapshot the live counters into state a few times a second (cheap scalars only; the heavy
    // percentile/chart work runs once when the run ends).
    const snapshot = useCallback(() => {
        const m = metricsRef.current;
        const elapsed = m.startAt ? (performance.now() - m.startAt) / 1000 : 0;
        setLive({
            count: m.count, ok: m.ok, fail: m.fail, bytesTotal: m.bytesTotal, inFlight: m.inFlight,
            elapsed, rps: elapsed > 0 ? m.count / elapsed : 0, avg: m.count ? m.sumDur / m.count : 0,
        });
    }, []);

    const finalize = useCallback((cfg) => {
        const m = metricsRef.current;
        const elapsed = m.startAt ? (performance.now() - m.startAt) / 1000 : 0;
        const sorted = [...m.durs].sort((a, b) => a - b);
        const maxDur = sorted.length ? sorted[sorted.length - 1] : 0;
        const p99 = percentile(sorted, 99);
        // Latency histogram: ~12 buckets up to p99, with the last as an overflow bucket.
        const buckets = 12;
        const span = (p99 || maxDur || 1) / (buckets - 1);
        const histLabels = Array.from({ length: buckets }, (_, i) => (i === buckets - 1 ? `${Math.round((buckets - 1) * span)}+` : `${Math.round(i * span)}`));
        const histValues = new Array(buckets).fill(0);
        sorted.forEach((d) => { histValues[Math.min(buckets - 1, Math.floor(d / (span || 1)))] += 1; });
        // Throughput per second.
        const seconds = Math.max(1, Math.ceil(elapsed));
        const rpsLabels = Array.from({ length: seconds }, (_, i) => `${i + 1}s`);
        const rpsValues = rpsLabels.map((_, i) => m.perSecond.get(i) || 0);
        const perTarget = [...m.perTarget.entries()].map(([id, t]) => {
            const ts = [...t.durs].sort((a, b) => a - b);
            return {
                id, label: targetById.get(id)?.label || id, count: t.count, fail: t.fail,
                avg: t.count ? t.sumDur / t.count : 0, p50: percentile(ts, 50), p95: percentile(ts, 95),
                max: ts.length ? ts[ts.length - 1] : 0, rps: elapsed > 0 ? t.count / elapsed : 0,
            };
        }).sort((a, b) => b.count - a.count);
        const summary = {
            at: Date.now(), callers: cfg.callers, model: cfg.model,
            durationS: cfg.durationS, countPerCaller: cfg.countPerCaller, thinkMs: cfg.thinkMs,
            bypassCache: cfg.bypassCache, targetIds: cfg.targets, elapsed,
            count: m.count, ok: m.ok, fail: m.fail, bytesTotal: m.bytesTotal,
            rps: elapsed > 0 ? m.count / elapsed : 0, avg: m.count ? m.sumDur / m.count : 0,
            p50: percentile(sorted, 50), p90: percentile(sorted, 90), p95: percentile(sorted, 95), p99, max: maxDur,
            errors: [...m.errors.entries()].sort((a, b) => b[1] - a[1]),
            perTarget, hist: { labels: histLabels, values: histValues }, rpsSeries: { labels: rpsLabels, values: rpsValues },
        };
        setResult(summary);
        setRuns((prev) => {
            const next = [{
                at: summary.at, callers: summary.callers, model: summary.model, durationS: summary.durationS,
                countPerCaller: summary.countPerCaller, count: summary.count, fail: summary.fail,
                rps: summary.rps, p95: summary.p95, elapsed: summary.elapsed,
                targets: summary.targetIds, bypassCache: summary.bypassCache,
            }, ...prev].slice(0, 15);
            writeJson(RUNS_KEY, next);
            return next;
        });
    }, [targetById]);

    const prepFullPool = useCallback(async (base, signal) => {
        for (let attempt = 0; attempt < 3; attempt += 1) {
            try {
                const res = await fetch(`${base}/listings/active/regso/${pick(STHLM_IDS)}?offset=0&limit=40&forSale=true`, { cache: 'no-store', signal });
                const data = await res.json();
                const rows = Array.isArray(data) ? data : (data.listings || data.rows || data.results || data.items || []);
                const found = [];
                rows.forEach((row) => {
                    const match = String(row?.listingUrl || '').match(/\/(annons|bostad)\/(\d+)/);
                    if (match) found.push(`${match[1]}/${match[2]}`);
                });
                if (found.length) return found;
            } catch (error) {
                if (signal.aborted) return [];
            }
        }
        return [];
    }, []);

    const record = (targetId, res) => {
        const m = metricsRef.current;
        m.count += 1;
        m.sumDur += res.dur;
        if (m.durs.length < MAX_SAMPLES) m.durs.push(res.dur);
        m.bytesTotal += res.bytes || 0;
        if (res.ok) m.ok += 1; else m.fail += 1;
        const secBucket = Math.floor((res.completedAt - m.startAt) / 1000);
        m.perSecond.set(secBucket, (m.perSecond.get(secBucket) || 0) + 1);
        if (!res.ok) {
            const key = res.error ? res.error : `HTTP ${res.status}`;
            m.errors.set(key, (m.errors.get(key) || 0) + 1);
        }
        let agg = m.perTarget.get(targetId);
        if (!agg) { agg = { count: 0, fail: 0, sumDur: 0, durs: [] }; m.perTarget.set(targetId, agg); }
        agg.count += 1;
        agg.sumDur += res.dur;
        if (!res.ok) agg.fail += 1;
        if (agg.durs.length < MAX_SAMPLES) agg.durs.push(res.dur);
    };

    const fireOne = async (target, signal, bypass) => {
        const spec = target.build();
        let url = spec.url;
        if (bypass && spec.mode !== 'no-cors') url += `${url.includes('?') ? '&' : '?'}_lt=${Math.random().toString(36).slice(2)}`;
        const opts = { method: spec.method, signal, cache: bypass ? 'no-store' : 'default' };
        if (spec.mode) opts.mode = spec.mode;
        if (spec.body) { opts.body = JSON.stringify(spec.body); opts.headers = { 'Content-Type': 'application/json' }; }
        const start = performance.now();
        const m = metricsRef.current;
        m.inFlight += 1;
        try {
            const res = await fetch(url, opts);
            let size = 0;
            if (spec.mode !== 'no-cors') { size = (await res.arrayBuffer()).byteLength; }
            return { ok: spec.mode === 'no-cors' ? true : res.ok, status: res.status, dur: performance.now() - start, bytes: size, completedAt: performance.now() };
        } catch (error) {
            if (error.name === 'AbortError') return null;
            return { ok: false, status: 0, dur: performance.now() - start, bytes: 0, error: error.message || 'network error', completedAt: performance.now() };
        } finally {
            m.inFlight -= 1;
        }
    };

    const start = async () => {
        const cfg = configRef.current;
        const chosen = cfg.targets.map((id) => targetById.get(id)).filter(Boolean);
        if (!chosen.length || running) return;
        const base = String(cfg.apiBase || DEFAULT_API_BASE).replace(/\/+$/, '');
        setResult(null);
        setPrepNote('');
        stopRef.current = false;
        abortRef.current = new AbortController();
        const { signal } = abortRef.current;
        metricsRef.current = freshMetrics();
        setRunning(true);

        // Prep the full-listing pool if that target is selected.
        if (chosen.some((t) => t.needsPool)) {
            setPrepNote('Fetching a sample of live listings for the popup test…');
            poolRef.current = await prepFullPool(base, signal);
            if (!poolRef.current.length) {
                setPrepNote('No live listings found — the "Listing popup" test is skipped this run.');
            } else {
                setPrepNote(`Popup test will hit ${poolRef.current.length} real listings.`);
            }
        }
        const usable = chosen.filter((t) => !t.needsPool || poolRef.current.length);
        if (!usable.length) { setRunning(false); return; }

        metricsRef.current.startAt = performance.now();
        const snapTimer = window.setInterval(snapshot, SNAPSHOT_MS);
        const endAt = cfg.model === 'duration' ? performance.now() + cfg.durationS * 1000 : Infinity;
        let rr = 0; // round-robin index shared across callers

        const caller = async (index) => {
            if (cfg.rampS > 0) await sleep((index / cfg.callers) * cfg.rampS * 1000, signal);
            let sent = 0;
            while (!stopRef.current) {
                if (cfg.model === 'duration' && performance.now() >= endAt) break;
                if (cfg.model === 'count' && sent >= cfg.countPerCaller) break;
                const target = usable[rr % usable.length];
                rr += 1;
                sent += 1;
                const res = await fireOne(target, signal, cfg.bypassCache);
                if (res) record(target.id, res);
                if (cfg.thinkMs > 0 && !stopRef.current) await sleep(cfg.thinkMs, signal);
            }
        };

        await Promise.all(Array.from({ length: cfg.callers }, (_, i) => caller(i)));
        window.clearInterval(snapTimer);
        snapshot();
        finalize(cfg);
        setRunning(false);
    };

    const stop = () => {
        stopRef.current = true;
        if (abortRef.current) abortRef.current.abort();
    };

    useEffect(() => () => { stopRef.current = true; if (abortRef.current) abortRef.current.abort(); }, []);

    const applyRun = (run) => set({
        callers: run.callers, model: run.model, durationS: run.durationS || DEFAULT_CONFIG.durationS,
        countPerCaller: run.countPerCaller || DEFAULT_CONFIG.countPerCaller,
        bypassCache: run.bypassCache !== false, targets: Array.isArray(run.targets) && run.targets.length ? run.targets : config.targets,
    });

    const tiles = result && [
        ['Requests', fmt(result.count), `${fmt(result.ok)} ok · ${fmt(result.fail)} failed`],
        ['Throughput', `${result.rps.toFixed(1)} /s`, `over ${duration(result.elapsed)}`],
        ['Success rate', `${result.count ? Math.round((100 * result.ok) / result.count) : 0}%`, `${bytes(result.bytesTotal)} transferred`],
        ['Median (p50)', ms(result.p50), `avg ${ms(result.avg)}`],
        ['p95', ms(result.p95), `p90 ${ms(result.p90)}`],
        ['p99 / max', ms(result.p99), `max ${ms(result.max)}`],
    ];

    return (
        <>
            <p className="ax-warning">
                These are real requests sent from your browser to the live site and API. Use it pre-launch only —
                high concurrency can slow the site for anyone using it. It runs client-side, so one browser is the
                ceiling (a few thousand requests a second over HTTP/2).
            </p>

            <section className="ax-card">
                <h3>Load test</h3>
                <div className="ax-perf-form">
                    <label className="ax-field ax-perf-base">
                        API base URL
                        <input
                            type="text"
                            value={config.apiBase}
                            onChange={(event) => set({ apiBase: event.target.value })}
                            disabled={running}
                            spellCheck={false}
                        />
                    </label>

                    <fieldset className="ax-perf-targets">
                        <legend>Endpoints to test {activeTargets.length > 1 ? '(callers cycle through them)' : ''}</legend>
                        <div className="ax-perf-target-grid">
                            {targets.map((target) => (
                                <label key={target.id} className={`ax-perf-target${config.targets.includes(target.id) ? ' is-on' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={config.targets.includes(target.id)}
                                        onChange={() => toggleTarget(target.id)}
                                        disabled={running}
                                    />
                                    <span>
                                        <strong>{target.label}</strong>
                                        <small>{target.hint}</small>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    <div className="ax-perf-row">
                        <label className="ax-field ax-perf-callers">
                            Simulated callers (concurrency)
                            <input
                                type="number"
                                min="1"
                                max={MAX_CALLERS}
                                value={config.callers}
                                onChange={(event) => set({ callers: Math.round(num(event.target.value, 1, MAX_CALLERS, 10)) })}
                                disabled={running}
                            />
                        </label>
                        <div className="ax-perf-presets" aria-hidden={running}>
                            {[1, 5, 10, 25, 50, 100, 200].map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`ax-pill ax-perf-preset${config.callers === value ? ' is-active' : ''}`}
                                    onClick={() => set({ callers: value })}
                                    disabled={running}
                                >
                                    {value}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="ax-perf-row">
                        <div className="ax-field">
                            Load model
                            <div className="ax-segmented-buttons">
                                <button type="button" className={config.model === 'duration' ? 'is-active' : ''} onClick={() => set({ model: 'duration' })} disabled={running}>
                                    For a duration
                                </button>
                                <button type="button" className={config.model === 'count' ? 'is-active' : ''} onClick={() => set({ model: 'count' })} disabled={running}>
                                    Fixed per caller
                                </button>
                            </div>
                        </div>
                        {config.model === 'duration' ? (
                            <label className="ax-field ax-perf-sm">
                                Duration (seconds)
                                <input type="number" min="1" max="600" value={config.durationS}
                                    onChange={(event) => set({ durationS: Math.round(num(event.target.value, 1, 600, 10)) })} disabled={running} />
                            </label>
                        ) : (
                            <label className="ax-field ax-perf-sm">
                                Requests per caller
                                <input type="number" min="1" max="100000" value={config.countPerCaller}
                                    onChange={(event) => set({ countPerCaller: Math.round(num(event.target.value, 1, 100000, 20)) })} disabled={running} />
                            </label>
                        )}
                        <label className="ax-field ax-perf-sm">
                            Think time (ms)
                            <input type="number" min="0" max="60000" value={config.thinkMs}
                                onChange={(event) => set({ thinkMs: Math.round(num(event.target.value, 0, 60000, 0)) })} disabled={running} />
                        </label>
                        <label className="ax-field ax-perf-sm">
                            Ramp-up (seconds)
                            <input type="number" min="0" max="120" value={config.rampS}
                                onChange={(event) => set({ rampS: Math.round(num(event.target.value, 0, 120, 0)) })} disabled={running} />
                        </label>
                    </div>

                    <label className="ax-check ax-check--small">
                        <input type="checkbox" checked={config.bypassCache} onChange={(event) => set({ bypassCache: event.target.checked })} disabled={running} />
                        Bypass caches (cache-buster + no-store) — hit the origin, not the browser/CDN cache
                    </label>

                    <div className="ax-actions">
                        {!running ? (
                            <button type="button" className="ax-button" onClick={start} disabled={!activeTargets.length}>
                                Run test
                            </button>
                        ) : (
                            <button type="button" className="ax-button ax-button--danger" onClick={stop}>Stop</button>
                        )}
                        <span className="ax-muted ax-perf-plan">
                            {activeTargets.length
                                ? (config.model === 'duration'
                                    ? `${config.callers} callers for ${config.durationS}s`
                                    : `${config.callers} callers × ${config.countPerCaller} = ${fmt(plannedTotal)} requests`)
                                : 'Pick at least one endpoint.'}
                        </span>
                    </div>
                    {prepNote && <p className="ax-hint ax-hint--tight">{prepNote}</p>}
                </div>
            </section>

            {(running || (live && !result)) && live && (
                <section className="ax-card">
                    <h3>Running…</h3>
                    {config.model === 'duration' && (
                        <div className="ax-progress">
                            <span className="ax-progress-fill" style={{ width: `${Math.min(100, (live.elapsed / config.durationS) * 100)}%` }} />
                            <span className="ax-progress-label">{duration(live.elapsed)} / {config.durationS}s</span>
                        </div>
                    )}
                    <div className="ax-live">
                        <div className="ax-live-counter"><div className="ax-tile-label">Requests</div><div className="ax-live-value">{fmt(live.count)}</div></div>
                        <div className="ax-live-counter"><div className="ax-tile-label">Throughput</div><div className="ax-live-value">{live.rps.toFixed(1)} <span className="ax-muted">/s</span></div></div>
                        <div className="ax-live-counter"><div className="ax-tile-label">In flight</div><div className="ax-live-value">{fmt(live.inFlight)}</div></div>
                        <div className="ax-live-counter"><div className="ax-tile-label">Failed</div><div className="ax-live-value">{fmt(live.fail)} <span className="ax-muted">avg {ms(live.avg)}</span></div></div>
                    </div>
                </section>
            )}

            {result && (
                <>
                    <div className="ax-tiles ax-tiles--three">
                        {tiles.map(([label, value, sub]) => (
                            <div key={label} className="ax-tile">
                                <div className="ax-tile-label">{label}</div>
                                <div className="ax-tile-value">{value}</div>
                                <div className="ax-tile-sub">{sub}</div>
                            </div>
                        ))}
                    </div>
                    <div className="ax-grid ax-grid--two">
                        <section className="ax-card">
                            <h3>Latency distribution (ms)</h3>
                            <BarChart labels={result.hist.labels} values={result.hist.values} ariaLabel="Latency distribution" />
                        </section>
                        <section className="ax-card">
                            <h3>Throughput per second</h3>
                            <BarChart labels={result.rpsSeries.labels} values={result.rpsSeries.values} ariaLabel="Requests completed per second" />
                        </section>
                    </div>
                    {result.perTarget.length > 1 && (
                        <section className="ax-card ax-table-card">
                            <div className="ax-table-meta"><span>Per endpoint</span></div>
                            <div className="ax-table-scroll">
                                <table className="ax-table">
                                    <thead>
                                        <tr><th>Endpoint</th><th className="num">Requests</th><th className="num">Failed</th><th className="num">/s</th><th className="num">avg</th><th className="num">p50</th><th className="num">p95</th><th className="num">max</th></tr>
                                    </thead>
                                    <tbody>
                                        {result.perTarget.map((row) => (
                                            <tr key={row.id}>
                                                <td>{row.label}</td>
                                                <td className="num">{fmt(row.count)}</td>
                                                <td className="num">{row.fail ? fmt(row.fail) : '—'}</td>
                                                <td className="num">{row.rps.toFixed(1)}</td>
                                                <td className="num">{ms(row.avg)}</td>
                                                <td className="num">{ms(row.p50)}</td>
                                                <td className="num">{ms(row.p95)}</td>
                                                <td className="num">{ms(row.max)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}
                    <section className="ax-card">
                        <h3>Errors</h3>
                        {result.errors.length ? (
                            <div className="ax-pills">
                                {result.errors.map(([key, value]) => (<span key={key} className="ax-pill">{key}: {fmt(value)}</span>))}
                            </div>
                        ) : <p className="ax-muted">No failed requests.</p>}
                    </section>
                </>
            )}

            {runs.length > 0 && (
                <section className="ax-card ax-table-card">
                    <div className="ax-table-meta"><span>Recent runs</span><span className="ax-muted">kept in this browser</span></div>
                    <div className="ax-table-scroll">
                        <table className="ax-table">
                            <thead>
                                <tr><th>When</th><th>Load</th><th className="num">Requests</th><th className="num">Failed</th><th className="num">/s</th><th className="num">p95</th><th aria-label="actions" /></tr>
                            </thead>
                            <tbody>
                                {runs.map((run) => (
                                    <tr key={run.at}>
                                        <td>{new Date(run.at).toLocaleTimeString('sv-SE')}</td>
                                        <td>{run.callers} × {run.model === 'duration' ? `${run.durationS}s` : `${run.countPerCaller}`}</td>
                                        <td className="num">{fmt(run.count)}</td>
                                        <td className="num">{run.fail ? fmt(run.fail) : '—'}</td>
                                        <td className="num">{run.rps.toFixed(1)}</td>
                                        <td className="num">{ms(run.p95)}</td>
                                        <td className="num"><button type="button" className="ax-link" onClick={() => applyRun(run)} disabled={running}>reuse</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </>
    );
};

export default PerformanceTab;
